import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { websiteSettingsAPI } from '../services/api';
import { WebsiteSettings } from '../types';
import { applyThemePreset } from '../utils/themePresets';
import i18n from '../i18n/config';

interface WebsiteSettingsContextType {
  settings: WebsiteSettings | null;
  loading: boolean;
  refreshSettings: () => Promise<void>;
}

const WebsiteSettingsContext = createContext<WebsiteSettingsContextType | undefined>(undefined);

export const useWebsiteSettings = () => {
  const context = useContext(WebsiteSettingsContext);
  if (!context) {
    throw new Error('useWebsiteSettings must be used within WebsiteSettingsProvider');
  }
  return context;
};

interface WebsiteSettingsProviderProps {
  children: ReactNode;
}

export const WebsiteSettingsProvider: React.FC<WebsiteSettingsProviderProps> = ({ children }) => {
  const [settings, setSettings] = useState<WebsiteSettings | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const data = await websiteSettingsAPI.getSettings();
      setSettings(data);
      
      // Apply default language if user has not explicitly set a language preference
      if (data.default_language && !localStorage.getItem('language')) {
        i18n.changeLanguage(data.default_language);
      }

      // Apply theme color preset dynamically
      if (data.theme_preset) {
        applyThemePreset(data.theme_preset);
      } else {
        applyThemePreset('wafi_crimson');
      }

      // Update document title
      if (data.site_name) {
        document.title = data.site_name;
      }
      
      // Update favicon if exists — replace ALL icon links so no stale icon flashes
      const links = document.querySelectorAll<HTMLLinkElement>("link[rel*='icon']");
      if (data.favicon_url) {
        links.forEach(link => { link.href = data.favicon_url!; });
        // If no link exists at all, create one
        if (links.length === 0) {
          const link = document.createElement('link');
          link.rel = 'icon';
          link.href = data.favicon_url;
          document.head.appendChild(link);
        }
      } else {
        links.forEach(link => { link.href = '/favicon.ico'; });
      }
    } catch (error) {
      console.error('Failed to fetch website settings:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const refreshSettings = async () => {
    await fetchSettings();
  };

  return (
    <WebsiteSettingsContext.Provider value={{ settings, loading, refreshSettings }}>
      {children}
    </WebsiteSettingsContext.Provider>
  );
};

export default WebsiteSettingsContext;
